/**
 * The channel list and the message history, read straight from GetStream with the
 * Support admin token.
 *
 * fs-backend does not proxy either one. It owns adoption metrics over the whole population
 * (see ./queries.ts) and GetStream owns the conversations, so listing, searching, sorting
 * and paging cost the API nothing and do not grow with the number of chats.
 *
 * Everything a row shows is already on the channel: fs-backend stamps `fsLatestInterviewId`
 * into channel custom data, keeps `frozen` in sync with chat_channel.state, and upserts
 * host and guest into GetStream as members with their name and avatar.
 */
import {
  Attachment,
  ChannelFilters,
  ChannelSort,
  MessageResponse,
  StreamChat,
  UserFilters,
} from 'stream-chat';

import {
  InterviewChatAttachment,
  InterviewChatChannelFilter,
  InterviewChatMessage,
  InterviewChatMessageRole,
  InterviewChatOrderBy,
  InterviewChatPerson,
  InterviewChatRow,
} from './types';

/** Matches ChatProviderChannelType.interview in fs-backend. */
const INTERVIEW_CHANNEL_TYPE = 'interview';

/** GetStream refuses a channel query past offset + limit > 1000. */
const MAX_CHANNEL_OFFSET = 1000;

/** GetStream refuses a channel query asking for more than this in one go. */
const MAX_CHANNELS_PER_QUERY = 30;

const MAX_SEARCHED_MEMBERS = 100;

type QueryChannelsParams = {
  filter: InterviewChatChannelFilter;
  orderBy: InterviewChatOrderBy;
  ascending: boolean;
  limit: number;
  offset: number;
};

export type ChannelsPage = {
  rows: InterviewChatRow[];
  /** True when a further page exists: a row past this page came back. */
  hasMore: boolean;
  /** True when paging has hit GetStream's offset ceiling and cannot go further. */
  atOffsetCeiling: boolean;
  /** True when more users match the name than one user query returns, so some chats are missing. */
  searchCapped: boolean;
};

/**
 * fs-backend writes these onto the channel when it provisions the pair, so the row needs no
 * Postgres lookup. See InterviewChannelCustomData.
 */
type InterviewChannelData = {
  fsInterviewHostUserId?: string;
  fsInterviewGuestUserId?: string;
  fsLatestInterviewId?: string;
};

const SORT_FIELD: Record<
  InterviewChatOrderBy,
  'last_message_at' | 'created_at'
> = {
  LastMessageAt: 'last_message_at',
  CreatedAt: 'created_at',
};

function personFrom(
  userId: string | undefined,
  members: { user?: { id?: string; name?: string; image?: unknown } }[]
): InterviewChatPerson {
  if (!userId) return { userId: null, name: null, avatarUrl: null };

  const user = members.find((member) => member.user?.id === userId)?.user;

  return {
    userId,
    name: user?.name ?? null,
    avatarUrl: typeof user?.image === 'string' ? user.image : null,
  };
}

function messageCountFrom(
  reported: unknown,
  loadedMessageCount: number
): number {
  const reportedCount = typeof reported === 'number' ? reported : 0;
  return Math.max(reportedCount, loadedMessageCount);
}

/**
 * Turns one GetStream channel into a row. `member.user.name` is what the search filter
 * matches on, so the name shown and the name searched are always the same value.
 */

function toChannelRow(entry: {
  channel: Record<string, any>;
  members?: { user?: { id?: string; name?: string; image?: unknown } }[];
  loadedMessageCount?: number;
}): InterviewChatRow {
  const channel = entry.channel as InterviewChannelData & Record<string, any>;
  const members = entry.members ?? [];

  return {
    providerChannelId: channel.id ?? '',
    providerChannelCid: channel.cid ?? '',
    // fs-backend sets frozen on every state change; interview chats only go Active ↔ Frozen.
    state: channel.frozen ? 'Frozen' : 'Active',
    createdAt: channel.created_at ?? null,
    latestInterviewId: channel.fsLatestInterviewId ?? null,
    host: personFrom(channel.fsInterviewHostUserId, members),
    guest: personFrom(channel.fsInterviewGuestUserId, members),
    messageCount: messageCountFrom(
      channel.message_count,
      entry.loadedMessageCount ?? 0
    ),
    lastMessageAt: channel.last_message_at ?? null,
  };
}

/**
 * GetStream's `member.user.name` channel filter matches nothing on interview channels, even
 * for an exact name and an admin token, while `members: { $in }` works. So names are matched
 * with a user query first and the channels are filtered by those ids.
 */
async function findMemberIdsByName(
  client: StreamChat,
  search: string
): Promise<string[]> {
  const { users } = await client.queryUsers(
    { name: { $autocomplete: search } } as UserFilters,
    {},
    { limit: MAX_SEARCHED_MEMBERS }
  );

  return users.map((user) => user.id);
}

export function buildChannelFilters(
  filter: InterviewChatChannelFilter
): ChannelFilters {
  const filters: Record<string, unknown> = { type: INTERVIEW_CHANNEL_TYPE };

  if (filter.memberIds) filters.members = { $in: filter.memberIds };

  const createdAt = [
    ...(filter.fromDate ? [{ created_at: { $gte: filter.fromDate } }] : []),
    ...(filter.toDate ? [{ created_at: { $lte: filter.toDate } }] : []),
  ];
  if (createdAt.length === 1) Object.assign(filters, createdAt[0]);
  if (createdAt.length > 1) filters.$and = createdAt;

  if (filter.frozen !== undefined) filters.frozen = filter.frozen;

  return filters as ChannelFilters;
}

/**
 * One page of interview channels. Paging, sorting and searching all happen inside
 * GetStream, so this is a fixed-cost call however many chats exist.
 *
 * Only two members are ever on an interview channel, hence `member_limit: 2`.
 *
 * Asks for one row past the page. GetStream sends no total, so that row is the only proof
 * a next page exists; a full page on its own is not one.
 */
export async function queryInterviewChannels(
  client: StreamChat,
  { filter, orderBy, ascending, limit, offset }: QueryChannelsParams
): Promise<ChannelsPage> {
  const probeLimit = limit + 1;
  if (probeLimit > MAX_CHANNELS_PER_QUERY) {
    throw new Error(
      `Page size ${limit} plus the probe row exceeds GetStream's ${MAX_CHANNELS_PER_QUERY} channels per query.`
    );
  }

  const atOffsetCeiling = offset + probeLimit > MAX_CHANNEL_OFFSET;
  if (atOffsetCeiling) {
    return {
      rows: [],
      hasMore: false,
      atOffsetCeiling: true,
      searchCapped: false,
    };
  }

  const search = filter.search?.trim();
  const memberIds = search
    ? await findMemberIdsByName(client, search)
    : undefined;

  const searchCapped = memberIds?.length === MAX_SEARCHED_MEMBERS;

  // GetStream rejects an empty `$in`.
  if (memberIds?.length === 0) {
    return {
      rows: [],
      hasMore: false,
      atOffsetCeiling: false,
      searchCapped: false,
    };
  }

  const sort = [
    { [SORT_FIELD[orderBy]]: ascending ? 1 : -1 },
  ] as unknown as ChannelSort;

  const response = await client.queryChannels(
    buildChannelFilters({ ...filter, memberIds }),
    sort,
    {
      limit: probeLimit,
      offset,
      message_limit: 100,
      member_limit: 2,
      watch: false,
      presence: false,
      // Must stay true: it is what hydrates `members`, which is where host and guest
      // names come from.
      state: true,
    }
  );

  const rows = response.slice(0, limit).map((channel) =>
    toChannelRow({
      channel: channel.data as Record<string, any>,
      members: Object.values(channel.state?.members ?? {}),
      loadedMessageCount: channel.state?.messages?.length ?? 0,
    })
  );

  return {
    rows,
    hasMore: response.length > limit,
    atOffsetCeiling: false,
    searchCapped,
  };
}

type FetchMessagesParams = {
  providerChannelId: string;
  host: InterviewChatPerson;
  guest: InterviewChatPerson;
  limit: number;
  /** Walks back into older history: returns only messages older than this id. */
  beforeId?: string | null;
};

export type ChannelMessagesPage = {
  items: InterviewChatMessage[];
  /** Cursor for the next, older page; null once the history is exhausted. */
  olderCursor: string | null;
};

function roleOf(
  message: MessageResponse,
  hostUserId: string | null,
  guestUserId: string | null
): InterviewChatMessageRole {
  if (message.type === 'system') return 'System';

  const senderId = message.user?.id ?? null;
  if (senderId && senderId === hostUserId) return 'Host';
  if (senderId && senderId === guestUserId) return 'Guest';

  return 'Other';
}

const IMAGE_ATTACHMENT_TYPES = new Set(['image', 'giphy']);

// Attachment fields are written by the sending client and end up in href/src.
function httpUrl(value: string | undefined): string | null {
  if (!value) return null;

  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:' ? value : null;
  } catch {
    return null;
  }
}

function mapAttachment(attachment: Attachment): InterviewChatAttachment {
  const imageUrl =
    httpUrl(attachment.image_url) ?? httpUrl(attachment.thumb_url);

  if (IMAGE_ATTACHMENT_TYPES.has(attachment.type ?? '') && imageUrl) {
    return {
      kind: 'image',
      url: imageUrl,
      thumbUrl: httpUrl(attachment.thumb_url) ?? imageUrl,
      name: attachment.fallback ?? attachment.title ?? null,
    };
  }

  return {
    kind: 'file',
    url:
      httpUrl(attachment.asset_url) ??
      httpUrl(attachment.title_link) ??
      imageUrl,
    thumbUrl: null,
    name: attachment.title ?? attachment.fallback ?? null,
  };
}

function mapMessage(
  message: MessageResponse,
  host: InterviewChatPerson,
  guest: InterviewChatPerson
): InterviewChatMessage {
  const role = roleOf(message, host.userId, guest.userId);
  const isDeleted = !!message.deleted_at || message.type === 'deleted';
  // Prefer the row's name/avatar: both sides come from the same channel member, so the
  // header and the bubbles never disagree.
  const person =
    role === 'Host' ? host : role === 'Guest' ? guest : (null as null);

  return {
    id: message.id,
    text: isDeleted ? null : message.text ?? null,
    createdAt: message.created_at
      ? new Date(message.created_at).toISOString()
      : new Date(0).toISOString(),
    role,
    userId: message.user?.id ?? null,
    userName: person?.name ?? message.user?.name ?? null,
    userAvatarUrl: person?.avatarUrl ?? (message.user?.image as string) ?? null,
    // A deleted message's attachments are gone with it; showing them would undo the delete.
    attachments: isDeleted
      ? []
      : (message.attachments ?? []).map(mapAttachment),
    isDeleted,
  };
}

/**
 * One page of a channel's history, oldest-first within the page.
 *
 * `client.channel()` registers the channel in the client's `activeChannels` map and
 * `query()` hydrates its state there. Nothing evicts that map, so browsing chat after
 * chat would grow the shared client for the life of the tab — hence the explicit
 * release of anything this call introduced.
 */
export async function fetchChannelMessages(
  client: StreamChat,
  { providerChannelId, host, guest, limit, beforeId }: FetchMessagesParams
): Promise<ChannelMessagesPage> {
  const cid = `${INTERVIEW_CHANNEL_TYPE}:${providerChannelId}`;
  const wasCached = cid in client.activeChannels;

  const channel = client.channel(INTERVIEW_CHANNEL_TYPE, providerChannelId);

  try {
    const response = await channel.query({
      messages: {
        limit,
        ...(beforeId ? { id_lt: beforeId } : {}),
      },
      // Must stay true: `state` is what makes GetStream return the messages at all.
      state: true,
      watch: false,
      presence: false,
    });

    const items = (response.messages ?? []).map((message) =>
      mapMessage(message, host, guest)
    );

    // A full page implies more behind it; the cursor is this page's oldest message.
    const hasMore = items.length === limit;

    return {
      items,
      olderCursor: hasMore && items.length ? items[0].id : null,
    };
  } finally {
    // Leave alone anything another part of the app had already cached.
    if (!wasCached) delete client.activeChannels[cid];
  }
}
